
import { useState } from "react";
import { X, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useMobileMenuData } from "@/hooks/useMobileMenuData";
import MobileMenuHeader from "./mobile/MobileMenuHeader";
import MobileMenuMainItems from "./mobile/MobileMenuMainItems";
import MobileMenuAuthContent from "./mobile/MobileMenuAuthContent";
import MobileMenuGuestContent from "./mobile/MobileMenuGuestContent";

import { AuthUserLite } from "@/types/domain";

interface MobileMenuProps {
  user: AuthUserLite | null;
  isAdmin: boolean;
  signOut: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const MobileMenu = ({ user, signOut, isOpen, setIsOpen }: MobileMenuProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const data = useMobileMenuData();
  
  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    setIsOpen(open);
  };

  const handleLinkClick = () => setSheetOpen(false);
  
  const handleSignOut = () => {
    signOut();
    setSheetOpen(false);
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Menu">
          {isOpen ? <X /> : <Menu />}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <MobileMenuHeader
          user={user}
          formattedTierName={data.getFormattedTierName()}
          badgeVariant={data.getBadgeVariant()}
          hasPendingVerification={data.hasPendingVerification}
        />
        <nav className="flex flex-col gap-2">
          <MobileMenuMainItems onLinkClick={handleLinkClick} />
          <Separator className="my-4" />
          {user ? (
            <MobileMenuAuthContent
              isAdmin={data.isAdmin}
              subscriptionStatus={data.subscriptionStatus || null}
              shouldShowUpgradeButton={data.shouldShowUpgradeButton}
              hasPendingVerification={data.hasPendingVerification}
              referralLink={data.getReferralLink()}
              onLinkClick={handleLinkClick}
              onSignOut={handleSignOut}
            />
          ) : (
            <MobileMenuGuestContent onLinkClick={handleLinkClick} />
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
